'use client'

import { useState, KeyboardEvent } from 'react'

import { Icon, Spacer, Text } from '@vinted/web-ui'
import { InfoCircle16 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { KeyboardKey, onA11yKeyDown } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { sellerProtectionScreenClickEvent } from '@marketplace-web/seller-protection/seller-protection-data'

import SellerProtectionInformationDialog from '../SellerProtectionInformationDialog'

type Props = {
  transactionId: number
  transactionStatus: number | null | undefined
  shipmentStatus: number | null | undefined
}

const HeroMessageSellerProtection = ({
  transactionId,
  transactionStatus,
  shipmentStatus,
}: Props) => {
  const translate = useTranslate('conversation.hero_message')
  const { track } = useTracking()

  const [isDialogVisible, setIsDialogVisible] = useState(false)

  const trackSellerProtectionClick = () => {
    track(
      sellerProtectionScreenClickEvent({
        screen: 'conversation',
        target: 'seller_protection_information',
        transactionId: transactionId.toString(),
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
        }),
      }),
    )
  }

  const openSellerProtectionDialog = () => {
    trackSellerProtectionClick()
    setIsDialogVisible(true)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    onA11yKeyDown(
      event,
      { keys: [KeyboardKey.Enter, KeyboardKey.Space] },
      () => openSellerProtectionDialog,
    )
  }

  return (
    <>
      <button
        className="u-flexbox u-align-items-center"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={openSellerProtectionDialog}
        type="button"
      >
        <Text
          text={translate('seller_protection_link')}
          as="span"
          clickable
          underline={false}
          type="subtitle"
        />
        <Spacer size="small" orientation="vertical" />
        <Icon name={InfoCircle16} color="primary-default" />
      </button>
      <SellerProtectionInformationDialog
        transactionId={transactionId.toString()}
        transactionStatus={transactionStatus}
        shipmentStatus={shipmentStatus}
        isVisible={isDialogVisible}
        onClose={() => setIsDialogVisible(false)}
      />
    </>
  )
}

export default HeroMessageSellerProtection
